import Head from 'next/head';

const getBaseTags = (props: {
  canonicalUrl: string;
  metaDescription: string;
  title: string;
  author: string;
}) => {
  return [
    <title key="title">{props.title}</title>,
    <meta key="description" name="description" content={props.metaDescription} />,
    <meta key="author" name="author" content={props.author} />,
    <link key="canonical" rel="canonical" href={props.canonicalUrl} />,
  ];
};

const getOgTags = (props?: {
  url: string;
  title: string;
  description: string;
  image: string;
  siteName: string;
  type: string;
}) => {
  if (!props) {
    return null;
  }
  const { url, title, description, image, siteName, type } = props;
  const tags = [
    <meta key="og:title" property="og:title" content={title} />,
    <meta key="og:url" property="og:url" content={url} />,
    <meta key="og:type" property="og:type" content={type} />,
    <meta key="og:description" property="og:description" content={description} />,
    <meta key="og:image" property="og:image" content={image} />,
    <meta key="og:site_name" property="og:site_name" content={siteName} />,
  ];
  return tags;
};

// https://easyschema.com/singlefamilyresidence/
type singleFamilySchemaType = {
  '@context': 'https://schema.org/';
  '@type': ['SingleFamilyResidence', 'Product'];
  name?: string;
  description?: string;
  image?: string;
  numberOfRooms?: string;
  occupancy?: {
    '@type': 'QuantitativeValue';
    minValue?: string;
    maxValue?: string;
  };
  floorSize?: {
    '@type': 'QuantitativeValue';
    value?: string;
    unitCode?: string;
  };
  leaseLength?: {
    '@type': 'QuantitativeValue';
    value?: string;
    unitText?: string;
  };
  numberOfBathroomsTotal?: string;
  numberOfBedrooms?: string;
  permittedUsage?: string;
  petsAllowed?: string;
  yearBuilt?: string;
  address?: {
    '@type': 'PostalAddress';
    addressCountry?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    streetAddress?: string;
    unitType?: string;
    unitNumber?: string;
  };
  telephone?: string;
  offers?: {
    '@type'?: string;
    priceCurrency?: string;
    price?: number;
    url?: string;
    availability?: string;
  };
};

const getPropertySchemaData = (props: {
  name?: string;
  description?: string;
  image?: string;
  numberOfRooms?: string;
  floorSize?: {
    value?: string;
    unitCode?: string;
  };
  numberOfBathroomsTotal?: string;
  numberOfBedrooms?: string;
  yearBuilt?: string;
  listPrice?: number;
  url?: string;
  address?: {
    addressCountry?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    streetAddress?: string;
  };
  lat?: number;
  lon?: number;
  hasSelfTour?: boolean;
  addressToken?: string;
}) => {
  const {
    address,
    floorSize,
    listPrice,
    url,
    lat,
    lon,
    hasSelfTour,
    addressToken,
    ...remainingProps
  } = props;
  const schemaData: singleFamilySchemaType = {
    '@context': 'https://schema.org/',
    '@type': ['SingleFamilyResidence', 'Product'],
    ...(address && {
      address: {
        '@type': 'PostalAddress',
        ...address,
      },
    }),
    ...(lat &&
      lon && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: lat,
          longitude: lon,
        },
      }),
    ...(floorSize && {
      floorSize: {
        '@type': 'QuantitativeValue',
        ...floorSize,
      },
    }),
    ...(hasSelfTour &&
      addressToken && {
        tourBookingPage: `https://consumer.opendoor.com/dashboard/buy/tour?id=${addressToken}`,
      }),
    ...(listPrice &&
      url && {
        offers: {
          '@type': 'Offer',
          priceCurrency: 'USD',
          price: listPrice,
          url,
          availability: 'http://schema.org/InStock',
        },
      }),
    ...remainingProps,
  };
  return schemaData;
};

const SeoHead = (props: {
  base: {
    title: string;
    canonicalUrl: string;
    metaDescription: string;
    author: string;
  };
  og?: {
    title: string;
    url: string;
    type: string;
    description: string;
    image: string;
    siteName: string;
  };
  schema?: {
    name?: string;
    description?: string;
    image?: string;
    numberOfRooms?: string;
    floorSize?: {
      value?: string;
      unitCode?: string;
    };
    numberOfBathroomsTotal?: string;
    numberOfBedrooms?: string;
    listPrice?: number;
    url?: string;
    yearBuilt?: string;
    address?: {
      addressCountry?: string;
      addressLocality?: string;
      addressRegion?: string;
      postalCode?: string;
      streetAddress?: string;
    };
    lat?: number;
    lon?: number;
    hasSelfTour?: boolean;
    addressToken?: string;
  };
}) => {
  const { base, og, schema } = props;
  // ensure that you don't nest tags inside of fragments
  // if you do that, then the next head component
  // will have issues with overrides
  return (
    <Head>
      {getBaseTags(base)}
      {getOgTags(og)}
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getPropertySchemaData(schema)),
          }}
        />
      )}
    </Head>
  );
};

export default SeoHead;
